import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import {
  Button,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { BFF_BASE_URL } from '../auth/authApi';
import { RootState } from '../redux/store';
import { useSelectedUser } from '../users/hooks/useSelectedUser';
import { LoadingButton } from '../shared/components/LoadingButton';
import { Section } from '../shared/components/Section';
import { GlowAlert } from '../shared/components/GlowAlert';

interface Reminder {
  user_id: string;
  type: 'summary' | 'status_prompt' | 'event_reminders';
  enabled: boolean;
  schedule_time: string | null;
  schedule_interval_minutes: number | null;
  next_run_at: string | null;
  timezone: string;
}

const LABELS: Record<Reminder['type'], { title: string; hint: string; empty: string; needs: string }> = {
  summary: {
    title: 'Daily summary',
    hint: 'Events of all the user\'s calendars for the next 7 days, plus open Zooit tasks and checklists due from yesterday through the week',
    empty: 'no events in the next 7 days and no open tasks due from yesterday on',
    needs: 'neither Zooit nor Google Calendar is',
  },
  status_prompt: {
    title: 'Nightly status prompt',
    hint: 'Asks how the day went and lists open Zooit tasks and checklists due yesterday, today or tomorrow, then older overdue ones, then the events of the rest of today and tomorrow',
    empty: 'no open tasks and no events for today or tomorrow',
    needs: 'neither Zooit nor Google Calendar is',
  },
  event_reminders: {
    title: 'Event reminders',
    hint: 'Timed events, on any of the user\'s calendars, starting within 2 hours: once when each reminder time (the event\'s own, else 30 and 10 minutes before) is reached. Send now reminds about all of them',
    empty: 'no timed events start in the next 2 hours',
    needs: 'Google Calendar is',
  },
};

const errorText = (e: any, fallback: string) => e.response?.data?.error || fallback;

const RemindersPage: React.FC = () => {
  const users = useSelector((state: RootState) => state.users.users);
  const [selectedUserId, setSelectedUserId] = useSelectedUser();
  // A remembered user who no longer exists is ignored; the backend then answers for its default admin.
  const requestedUserId = users.some((u) => u.id === selectedUserId) ? selectedUserId : '';
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // What is being saved or sent right now (`save:<type>` / `send:<type>`)
  const [pending, setPending] = useState<string[]>([]);
  const begin = (key: string) => setPending((p) => [...p, key]);
  const end = (key: string) => setPending((p) => p.filter((k) => k !== key));
  // What is typed in a field and not yet saved, by reminder type
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const userParams = requestedUserId ? { user_id: requestedUserId } : undefined;
  // Whose reminders these are, as the backend resolved it
  const userId = requestedUserId || reminders[0]?.user_id || '';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BFF_BASE_URL}/admin/reminders`, { params: userParams });
      setReminders(response.data);
      setDrafts({});
      setError(null);
    } catch (e) {
      setError(errorText(e, 'Failed to fetch reminders'));
    } finally {
      setLoading(false);
    }
  }, [requestedUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  const save = async (reminder: Reminder, changes: Partial<Reminder>) => {
    const key = `save:${reminder.type}`;
    setNotice(null);
    begin(key);
    try {
      const response = await axios.put(`${BFF_BASE_URL}/admin/reminders/${reminder.type}`, changes, {
        params: userParams,
      });
      setReminders((all) => all.map((r) => (r.type === reminder.type ? response.data : r)));
      setDrafts((d) => {
        const rest = { ...d };
        delete rest[reminder.type];
        return rest;
      });
      setError(null);
    } catch (e) {
      setError(errorText(e, 'Failed to save reminder'));
    } finally {
      end(key);
    }
  };

  const sendNow = async (reminder: Reminder) => {
    const key = `send:${reminder.type}`;
    setNotice(null);
    begin(key);
    try {
      const response = await axios.post(`${BFF_BASE_URL}/admin/reminders/${reminder.type}/send`, undefined, {
        params: userParams,
      });
      const { title, empty, needs } = LABELS[reminder.type];
      if (response.data.sent) {
        setNotice(`${title} sent.`);
        setError(null);
      } else if (response.data.reason === 'not_connected') {
        // Not a "nothing to send": the user's connection is gone, so nothing was even looked at
        setNotice(null);
        setError(`${title} not sent: ${needs} not connected for this user. They need to reconnect it in the tools page.`);
      } else {
        setNotice(`${title}: nothing to send (${empty}).`);
        setError(null);
      }
    } catch (e) {
      setError(errorText(e, 'Failed to send reminder'));
    } finally {
      end(key);
    }
  };

  const commit = (reminder: Reminder) => {
    const draft = drafts[reminder.type];
    if (draft === undefined || draft === '') return;
    if (reminder.type === 'event_reminders') {
      save(reminder, { schedule_interval_minutes: Number(draft) });
    } else {
      save(reminder, { schedule_time: draft });
    }
  };

  return (
    <Section
      title="Reminders"
      subtitle={`Changes apply immediately. Times are ${reminders[0]?.timezone ?? 'local'} time.`}
      onRefresh={load}
      refreshing={loading}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          User:
        </Typography>
        <Select
          size="small"
          value={users.some((u) => u.id === userId) ? userId : ''}
          onChange={(e) => setSelectedUserId(e.target.value)}
          sx={{ minWidth: 220 }}
          inputProps={{ 'aria-label': 'User' }}
        >
          {users.map((user) => (
            <MenuItem key={user.id} value={user.id}>
              {user.display_name || user.email}
              {user.phone_number ? ` (${user.phone_number})` : ''}
            </MenuItem>
          ))}
        </Select>
      </Stack>
      {error && <GlowAlert sx={{ mb: 2 }}>{error}</GlowAlert>}
      {notice && (
        <GlowAlert severity="info" sx={{ mb: 2 }}>
          {notice}
        </GlowAlert>
      )}
      {loading && reminders.length === 0 && <GlowAlert severity="info">Loading reminders...</GlowAlert>}

      {reminders.length > 0 && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Reminder</TableCell>
                <TableCell>Enabled</TableCell>
                <TableCell>Runs</TableCell>
                <TableCell>Next run</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {reminders.map((reminder) => {
                const isInterval = reminder.type === 'event_reminders';
                const saved = isInterval
                  ? String(reminder.schedule_interval_minutes ?? '')
                  : reminder.schedule_time ?? '';
                const value = drafts[reminder.type] ?? saved;
                const label = LABELS[reminder.type];
                return (
                  <TableRow key={reminder.type}>
                    <TableCell>
                      <Typography variant="body2">{label.title}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {label.hint}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={reminder.enabled}
                        onChange={(e) => save(reminder, { enabled: e.target.checked })}
                        disabled={pending.includes(`save:${reminder.type}`)}
                        inputProps={{ 'aria-label': `${label.title} enabled` }}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {isInterval && <Typography variant="body2">every</Typography>}
                        <TextField
                          size="small"
                          type={isInterval ? 'number' : 'time'}
                          value={value}
                          onChange={(e) => setDrafts((d) => ({ ...d, [reminder.type]: e.target.value }))}
                          onBlur={() => commit(reminder)}
                          onKeyDown={(e) => e.key === 'Enter' && commit(reminder)}
                          disabled={pending.includes(`save:${reminder.type}`)}
                          inputProps={{
                            'aria-label': `${label.title} ${isInterval ? 'interval' : 'time'}`,
                            ...(isInterval ? { min: 1, max: 1440 } : {}),
                          }}
                          sx={{ width: isInterval ? 90 : 130 }}
                        />
                        {isInterval && <Typography variant="body2">min</Typography>}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {reminder.next_run_at ? new Date(reminder.next_run_at).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <LoadingButton
                        size="small"
                        onClick={() => sendNow(reminder)}
                        loading={pending.includes(`send:${reminder.type}`)}
                      >
                        Send now
                      </LoadingButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Section>
  );
};

export default RemindersPage;
